import { ParseError, Parser, TreeNode } from '../types';

/**
 * Base abstract class for parsers providing common functionality
 */
export abstract class BaseParser implements Parser {
  abstract readonly name: string;
  abstract canParse(input: string): boolean;

  /**
   * Public parse method with error handling
   */
  parse(input: string): TreeNode[] {
    try {
      return this.parseInternal(input);
    } catch (error) {
      throw new ParseError(
        `Failed to parse with ${this.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        this.name,
        input.substring(0, 100)
      );
    }
  }

  /**
   * Internal parsing logic to be implemented by each parser
   */
  protected abstract parseInternal(input: string): TreeNode[];

  protected createNode(label: string): TreeNode {
    return {
      label: label.trim(),
      nodes: []
    };
  }

  protected detectIndentSize(lines: string[]): number {
    for (const line of lines) {
      let spaces = 0;
      for (const char of line) {
        if (char === ' ') spaces++;
        else break;
      }
      if (spaces > 0) {
        return spaces >= 4 ? 4 : 2;
      }
    }
    return 2; // default
  }

  protected buildHierarchy(
    lines: string[],
    getLevelFn: (line: string) => number,
    getLabelFn: (line: string, level: number) => string
  ): TreeNode[] {
    const nodes: TreeNode[] = [];
    const levelStack: TreeNode[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      const level = getLevelFn(line);
      const label = getLabelFn(line, level);

      if (!label) continue;

      // Trim stack to current level
      levelStack.length = level;

      const node = this.createNode(label);

      // Add to appropriate parent or root
      if (level === 0) {
        nodes.push(node);
      } else if (levelStack[level - 1]) {
        levelStack[level - 1].nodes.push(node);
      } else {
        // Fallback for malformed hierarchy
        nodes.push(node);
      }

      // Update level stack
      levelStack[level] = node;
    }

    return nodes;
  }
}
